import React, { Fragment, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col } from "react-bootstrap";
import moment from "moment-timezone";

// Transform Airflow data to match the expected format
function transformAirflowData(airflowData) {
  console.log("Transforming Airflow data for Cluster Pool 2...");
  console.log("airflowData received:", airflowData);
  
  const result = {
    Warranty: {},
    GTM: {},
  };

  if (!Array.isArray(airflowData)) {
    console.log("airflowData is not an array:", typeof airflowData);
    return result;
  }

  console.log("Processing", airflowData.length, "DAGs");

  airflowData.forEach((dag, index) => {
    console.log(`Processing DAG ${index + 1}:`, dag.dag_id);
    
    // Match DAG IDs
    let colKey = null;
    const dagId = dag.dag_id.toLowerCase();
    
    if (dagId.includes('warranty')) {
      colKey = 'Warranty';
    } else if (dagId.includes('dmt') || dagId.includes('gtm')) {
      colKey = 'GTM';
    }
    
    if (!colKey) {
      console.log(`Skipping DAG: ${dag.dag_id}`);
      return;
    }

    console.log(`DAG ${dag.dag_id} -> ${colKey}`);

    if (Array.isArray(dag.runs) && dag.runs.length > 0) {
      console.log(`Processing ${dag.runs.length} runs`);
      
      // Get today's date as string
      const todayEST = moment.tz("America/New_York").format('YYYY-MM-DD'); // Today in EST
      console.log("Today in EST:", todayEST);
      
      // Filter for today's runs (convert UTC to EST first)
      const todaysRuns = dag.runs.filter(run => {
        const runTimeUTC = moment.utc(run.start_date);
        const runTimeEST = runTimeUTC.tz("America/New_York");
        const runDateEST = runTimeEST.format('YYYY-MM-DD');
        const isToday = runDateEST === todayEST;
        console.log(`${run.run_id}: UTC ${run.start_date} -> EST ${runTimeEST.format()} -> Date: ${runDateEST} (Today: ${isToday})`);
        return isToday;
      });
      
      console.log(`Found ${todaysRuns.length} runs for today`);
      
      if (todaysRuns.length === 0) return;
      
      // Group by hour and get latest run
      const runsByHour = {};
      
      todaysRuns.forEach(run => {
        const runTimeUTC = moment.utc(run.start_date);
        const runTimeEST = runTimeUTC.tz("America/New_York");
        const hour = runTimeEST.hour();
        const hourKey = `${hour % 12 || 12}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
        const displayTime = `${hour % 12 || 12}:${runTimeEST.minute().toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
        
        console.log(`Run ${run.run_id}: EST ${runTimeEST.format()} -> ${displayTime} -> Hour: ${hourKey}`);
        console.log(`Hour key format: "${hourKey}"`);
        
        // Keep the latest run for this hour
        if (!runsByHour[hourKey] || runTimeEST.isAfter(moment.utc(runsByHour[hourKey].start_date).tz("America/New_York"))) {
          runsByHour[hourKey] = { ...run, displayTime, hourKey };
          console.log(`Latest for ${hourKey}: ${run.run_id} (${run.state})`);
        }
      });
      
      // Add to result
      Object.values(runsByHour).forEach(latestRun => {
        let status = latestRun.state || 'N/A';
        if (status === 'success') status = 'Completed';
        else if (status === 'failed') status = 'Failed';
        else if (status === 'running') status = 'Running';
        
        console.log(`Adding to ${colKey} at ${latestRun.hourKey}: ${status}`);
        result[colKey][latestRun.hourKey] = {
          status,
          runtime: latestRun.duration || '-',
          displayTime: latestRun.displayTime,
          runId: latestRun.run_id
        };
      });
      
    } else {
      console.log(`No runs for DAG ${dag.dag_id}`);
    }
  });
  
  console.log("Final result:", result);
  return result;
}

const AirflowClusterPoolTwo = ({ dynamicData }) => {
  // Fallback static data if no dynamicData provided
  const staticData = {
    Warranty: {
      "12:00 AM": {
        status: "Completed",
        runtime: "1 Hr 2 Min",
        displayTime: "12:00 AM",
      },
    },
    GTM: {},
  };

  // Use dynamic data if available
  const data = Array.isArray(dynamicData)
    ? transformAirflowData(dynamicData)
    : staticData;

  // Set our reference timezone
  const nowEST = moment.tz("America/New_York");

  // Generate time slots for the entire day (midnight to 11:00 PM) in EST
  const timeSlots = useMemo(() => {
    const slots = [];
    const start = moment.tz("America/New_York").startOf("day");
    for (let i = 0; i < 24; i++) {
      slots.push(start.clone().add(i, "hours").format("h:mm A"));
    }
    return slots;
  }, []);

  const columns = [
    {
      title: "Warranty",
      schedule: "",
      dataKey: "Warranty",
    },
    {
      title: "GTM",
      schedule: "",
      dataKey: "GTM",
    },
  ];

  // Calculate the current hour in EST
  const currentHour = nowEST.clone().startOf("hour").format("h:mm A");
  const currentRowRef = useRef(null);

  useEffect(() => {
    if (currentRowRef.current) {
      currentRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, []);

  // Helper: Map status to styles
  const getStatusStyle = (status) => {
    if (!status) return {};
    switch (status.toLowerCase()) {
      case "completed":
        return { color: "green", fontWeight: "bold" };
      case "running":
      case "in progress":
        return { color: "blue", fontWeight: "bold" };
      case "failed":
      case "cancelled":
        return { color: "red", fontWeight: "bold" };
      case "scheduled":
        return { color: "#FFD700", fontWeight: "bold" };
      default:
        return {};
    }
  };

  // Retrieve run data for each column and time slot
  const getDataForTime = (colKey, slotTime) => {
    // First, check if there's an exact match in the dynamic data
    const exact = data[colKey]?.[slotTime];
    if (exact) {
      console.log(`Found exact match for ${slotTime}:`, exact);
      return exact;
    }

    // Otherwise, attempt to match by the hour in EST
    const keys = Object.keys(data[colKey] || {});
    const slotHour = moment.tz(slotTime, "h:mm A", "America/New_York").hour();
    for (const key of keys) {
      const keyHour = moment.tz(key, "h:mm A", "America/New_York").hour();
      if (keyHour === slotHour) {
        console.log(`Found hour match: ${key} for ${slotTime}`);
        return data[colKey][key];
      }
    }

    // Build a moment object for the current slot today in EST
    const slotMoment = moment.tz(
      `${nowEST.format("M/D/YYYY")} ${slotTime}`,
      "M/D/YYYY h:mm A",
      "America/New_York"
    );

    // If this column runs every hour and the slot is in the future, mark as "Scheduled"
    if (["Warranty", "GTM"].includes(colKey)) {
      if (slotMoment.isAfter(nowEST)) {
        return {
          status: "Scheduled",
          runtime: "-",
          displayTime: slotTime,
        };
      }
    }

    // Otherwise, if it's in the past or there's just no data, return the default dash
    return {
      status: "-",
      runtime: "-",
      displayTime: slotTime,
    };
  };

  return (
    <Fragment>
      <Row className="mt-5">
        <Col lg={12}>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <Card.Header>
              <Card.Title className="text-center" style={{ color: "#3D4465" }}>
                Cluster Pool 2
              </Card.Title>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "1.1rem",
                  marginTop: "5px",
                  color: "#3D4465",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {moment().format("YYYY-MM-DD HH:mm:ss")}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Table
                  bordered
                  style={{ borderRadius: "8px", marginBottom: 0 }}
                >
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.title}
                          colSpan={3}
                          style={{
                            textAlign: "center",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#3D4465",
                            color: "white",
                            zIndex: 4,
                            padding: "8px",
                          }}
                        >
                          <div style={{ fontWeight: "bold" }}>{col.title}</div>
                          <div
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "0.85rem",
                            }}
                          >
                            {col.schedule}
                          </div>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {columns.map((col) => (
                        <React.Fragment key={col.title}>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Runs
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Run time
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time) => {
                      const isCurrentRow = time === currentHour;
                      const rowStyle = isCurrentRow
                        ? {
                            position: "sticky",
                            top: "80px",
                            backgroundColor: "#d1e7dd",
                            zIndex: 2,
                          }
                        : {};
                      return (
                        <tr
                          key={time}
                          style={rowStyle}
                          ref={isCurrentRow ? currentRowRef : null}
                        >
                          {columns.map((col) => {
                            const cellData = getDataForTime(col.dataKey, time);
                            const displayTime = cellData.displayTime || time;
                            return (
                              <React.Fragment key={`${col.title}-${time}`}>
                                <td style={{ textAlign: "center" }}>
                                  {displayTime}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span style={getStatusStyle(cellData.status)}>
                                    {cellData.status === "Completed"
                                      ? "Succeeded"
                                      : cellData.status}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {cellData.runtime}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AirflowClusterPoolTwo; 